import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Phone, Clock, User, Bot, FileText } from "lucide-react";

function parseTranscript(t) {
  if (!t) return [];
  // Support formats like "Agent: ...\nUser: ..." or "AI: ..." etc.
  const lines = t.split(/\r?\n/).filter(Boolean);
  const parsed = [];
  for (const line of lines) {
    const m = line.match(/^(Agent|Assistant|AI|Bot|User|Customer|Caller)\s*[:\-]\s*(.+)$/i);
    if (m) {
      parsed.push({ role: /agent|assistant|ai|bot/i.test(m[1]) ? "agent" : "user", text: m[2] });
    } else if (parsed.length) {
      parsed[parsed.length - 1].text += " " + line;
    } else {
      parsed.push({ role: "agent", text: line });
    }
  }
  return parsed;
}

export function TranscriptModal({ call, open, onOpenChange }) {
  const messages = parseTranscript(call?.transcript);
  const dur = call?.duration_seconds ? `${Math.floor(call.duration_seconds / 60)}m ${call.duration_seconds % 60}s` : "—";
  const callerLabel = call?.customer_phone || (call?.customer_name ? call.customer_name : "Web Call");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] p-0 flex flex-col overflow-hidden" data-testid="transcript-modal">
        <DialogHeader className="px-6 pt-6 pb-3 shrink-0">
          <DialogTitle className="flex items-center gap-2">Call Transcript</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-3 text-xs">
            <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {callerLabel}</span>
            {call?.customer_phone && call?.customer_name && (
              <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {call.customer_phone}</span>
            )}
            <span className="inline-flex items-center gap-1"><Bot className="w-3 h-3" /> {call?.agent_name || "Agent"}</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {dur}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-4 space-y-3" data-testid="transcript-scroll">
          {/* Call Summary — always render section header even when empty */}
          <div className="rounded-lg border border-primary/25 bg-primary/[0.04] p-4" data-testid="call-summary-section">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-3.5 h-3.5 text-primary" />
              <div className="text-[10px] uppercase tracking-[0.15em] text-primary font-semibold">Call Summary</div>
            </div>
            {call?.summary ? (
              <p className="text-sm leading-relaxed">{call.summary}</p>
            ) : (
              <p className="text-sm text-muted-foreground italic">No AI summary was generated for this call.</p>
            )}
          </div>

          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold pt-1">Transcript</div>
          {messages.length === 0 ? (
            <p className="text-sm text-muted-foreground italic py-4 text-center">No transcript available.</p>
          ) : (
            <div className="space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-2.5 ${m.role === "agent" ? "" : "flex-row-reverse"}`}>
                  <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center border ${m.role === "agent" ? "bg-primary/10 border-primary/30 text-primary" : "bg-muted border-border"}`}>
                    {m.role === "agent" ? <Bot className="w-3.5 h-3.5" /> : <User className="w-3.5 h-3.5" />}
                  </div>
                  <div className={`max-w-[80%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "agent" ? "bg-primary/5 border border-primary/15" : "bg-muted border border-border"}`}>
                    <div className="text-[10px] uppercase tracking-wider mb-1 opacity-70">{m.role}</div>
                    {m.text}
                  </div>
                </div>
              ))}
            </div>
          )}

          {call?.structured_data && Object.keys(call.structured_data).length > 0 && (
            <div className="rounded-lg border border-border bg-muted/40 p-3 mt-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Structured Data</div>
              <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                {Object.entries(call.structured_data).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-border/50 py-1">
                    <span className="text-muted-foreground">{k}</span>
                    <span className="font-semibold truncate ml-2">{String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function LeadStatusBadge({ status }) {
  const map = {
    new: { label: "New", cls: "bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/25" },
    contacted: { label: "Contacted", cls: "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border-indigo-500/25" },
    qualified: { label: "Qualified", cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/25" },
    won: { label: "Won", cls: "bg-teal-500/15 text-teal-600 dark:text-teal-400 border-teal-500/25" },
    lost: { label: "Lost", cls: "bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/25" },
    follow_up: { label: "Follow-up", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/25" },
    unqualified: { label: "Unqualified", cls: "bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/25" },
  };
  const cfg = map[status] || map.new;
  return (
    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider font-semibold ${cfg.cls}`} data-testid={`status-badge-${status}`}>
      {cfg.label}
    </Badge>
  );
}

export const LEAD_STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "qualified", label: "Qualified" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
  { value: "follow_up", label: "Follow-up" },
  { value: "unqualified", label: "Unqualified" },
];
